import React, { useCallback, useMemo, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import useEventTemplates from '../hooks/useEventTemplates';

import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';
import useV3Offers from '../hooks/useV3Offers';
import useV3Budgets from '../hooks/useV3Budgets';
import useV3LineupActions from '../hooks/useV3LineupActions';
import useV3OfferActions from '../hooks/useV3OfferActions';
import useV3OfferBudget, { formatCalculatedNumber } from '../hooks/useV3OfferBudget';
import useV3OfferSend from '../hooks/useV3OfferSend';
import { collectOfferPerformers } from '../utils/offerPerformers';

import V3MobileOfferBreadcrumb from './mobile/V3MobileOfferBreadcrumb';
import V3MobileActionSheet from './mobile/V3MobileActionSheet';
import V3MobilePerformerPickerSheet from './mobile/V3MobilePerformerPickerSheet';
import V3MobileLineupStrip from './mobile/V3MobileLineupStrip';
import V3MobileLineupReorderSheet from './mobile/V3MobileLineupReorderSheet';
import V3MobileCreateHoldSheet from './mobile/V3MobileCreateHoldSheet';
import V3OfferDealTermsPaneBody from './V3OfferDealTermsPaneBody';
import IncomeSection from './IncomeSection';
import ExpensesSection from './ExpensesSection';
import EventTemplateBar from './EventTemplateBar';
import V3OfferSeatMapPicker from './V3OfferSeatMapPicker';
import V3FinancialSummarySection from './V3FinancialSummarySection';
import V3SendOfferModal from './V3SendOfferModal';
import CopyOfferModal from './CopyOfferModal';
import EventDetails from '../../EventDetails/components/EventDetails';

import '../styles/mobile.css';

const V3FinancePageMobile = () => {
  const {
    csrfToken, team, currentUser, offer, budget, lineItems,
    eventOffers, confirm, dispatch,
  } = useV3OfferContext();

  const performers = useMemo(() => collectOfferPerformers(offer, eventOffers), [offer, eventOffers]);

  const { updateOffer } = useV3Offers();
  const { reorderLineItem } = useV3Budgets();

  const {
    handlePreview, handleCopy, handleDownload, handleDelete, handleChangeActType,
    handleEditPrimaryPerformer, handleCreateHold,
    copyModalOpen, isCopying, handleSubmitCopy, handleCancelCopy,
  } = useV3OfferActions();

  const {
    handleSelectOffer, handleReorder,
    handlePickNewArtist, selectingOfferId,
  } = useV3LineupActions();

  const {
    budgetForComponents,
    siblingGuaranteeExpenses,
    budgetForMath,
    calculated,
    eventVenueNet,
    hasSecondaryCurrencySymbol,
    globalCurrency,
    showCurrencyConversion,
    handleUpdateBudget,
    handleAutoSaveLineItem,
    handleDeleteLineItem,
    handleCreateTicketLineItem,
    handleCreateIncomeLineItem,
    handleCreateAdditionLineItem,
    handleCreateExpenseLineItem,
    handleCreateDeductionLineItem,
    handleCreateBeforeAdjustedDeductionsLineItem,
    handleCreateAncillaryIncomeLineItem,
    handleCreateAncillaryExpenseLineItem,
  } = useV3OfferBudget();

  const {
    showSendModal,
    setShowSendModal,
    sendTo,
    setSendTo,
    sendMessage,
    setSendMessage,
    isSendingOffer,
    handleSendOffer,
    handleSubmitSend,
  } = useV3OfferSend();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [reorderSheetOpen, setReorderSheetOpen] = useState(false);
  const [holdSheetOpen, setHoldSheetOpen] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  // Picker sheet opens from the empty-state header (mode='edit'), the
  // Actions menu Add/Change row (mode='edit'), and the lineup strip "+"
  // chip (mode='add-sibling'). One sheet, two submit handlers.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState('edit');
  const openPickerForEdit = useCallback(() => {
    setPickerMode('edit');
    setPickerOpen(true);
  }, []);
  const openPickerForAdd = useCallback(() => {
    setPickerMode('add-sibling');
    setPickerOpen(true);
  }, []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const handlePickerSubmit = useCallback(async (picked) => {
    if (pickerMode === 'edit') {
      await handleEditPrimaryPerformer(picked);
    } else {
      const added = await handlePickNewArtist(picked);
      if (!added) return;
    }
    setPickerOpen(false);
  }, [pickerMode, handleEditPrimaryPerformer, handlePickNewArtist]);

  const { eventTemplates, upsertEventTemplate } = useEventTemplates(team.id);

  const patchBudget = useCallback((payload) => {
    dispatch({ type: V3_ACTIONS.UPDATE_BUDGET, payload });
  }, [dispatch]);

  const handleBack = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = `/teams/${team.id}/offers/v3`;
    }
  }, [team]);

  const sheetGroups = useMemo(() => {
    const performerSlot = offer?.primary_performer
      ? {
        id: 'change-artist',
        label: 'Change artist',
        icon: 'far fa-exchange',
        onClick: openPickerForEdit,
      }
      : {
        id: 'add-artist',
        label: 'Add artist',
        icon: 'far fa-plus',
        onClick: openPickerForEdit,
        brand: true,
      };

    const coreGroup = [
      { id: 'send',     label: 'Send Offer',   icon: 'far fa-paper-plane',     onClick: handleSendOffer },
      { id: 'preview',  label: 'Preview',      icon: 'far fa-eye',             onClick: handlePreview },
      { id: 'copy',     label: 'Copy',         icon: 'far fa-copy',            onClick: handleCopy },
      { id: 'download', label: 'Download PDF', icon: 'far fa-arrow-to-bottom', onClick: handleDownload },
    ];
    if (confirm) {
      coreGroup.push({
        id: 'event-details',
        label: 'View event details',
        icon: 'far fa-calendar',
        onClick: () => setEventDetailsOpen(true),
      });
    }

    return [
      [performerSlot],
      [{
        id: 'create-hold',
        label: 'Create hold',
        icon: 'far fa-calendar-plus',
        onClick: () => setHoldSheetOpen(true),
      }],
      coreGroup,
      [{ id: 'delete', label: 'Delete', icon: 'far fa-trash', onClick: handleDelete, destructive: true }],
    ];
  }, [
    offer,
    confirm,
    openPickerForEdit,
    handleSendOffer,
    handlePreview,
    handleCopy,
    handleDownload,
    handleDelete,
  ]);

  const currencyToggleProps = hasSecondaryCurrencySymbol ? {
    enabled: !!offer?.display_currency_conversion_for_offer,
    onToggle: () => {
      updateOffer(offer.id, {
        display_currency_conversion_for_offer: !offer.display_currency_conversion_for_offer,
      });
    },
    label: team.secondary_currency_symbol,
    tooltip: `Display currency conversion\n1 ${team.base_currency_symbol} = ${team.currency_conversion_rate} ${team.secondary_currency_symbol}`,
  } : null;

  if (!offer || !budget) {
    return (
      <div className="v3-mobile-page">
        <div className="text-center py-5">
          <i className="fas fa-spinner fa-spin mr-2" />
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="v3-mobile-page">
      <ToastContainer icon={false} />

      <V3MobileOfferBreadcrumb
        onBack={handleBack}
        onOpenSheet={() => setSheetOpen(true)}
      />

      <V3MobileLineupStrip
        lineup={offer?.lineup || []}
        activeOfferId={offer?.id}
        selectingOfferId={selectingOfferId}
        onSelect={handleSelectOffer}
        onAddArtist={openPickerForAdd}
        onLongPressChip={() => setReorderSheetOpen(true)}
      />

      <EventTemplateBar
        team={team}
        csrfToken={csrfToken}
        budget={budgetForComponents}
        eventTemplates={eventTemplates}
        onApplyTemplate={() => window.location.reload()}
        onTemplateCreated={upsertEventTemplate}
        onTemplateSaved={upsertEventTemplate}
        onBudgetPatched={patchBudget}
      />

      <V3OfferSeatMapPicker />

      <div
        className="v3-mobile-offer-card"
        data-loading={selectingOfferId ? 'true' : 'false'}
      >
        <V3OfferDealTermsPaneBody
          team={team}
          offer={offer}
          budget={budgetForComponents}
          confirm={confirm}
          csrfToken={csrfToken}
          actType={offer?.primary_performer?.act_type || offer?.act_type}
          showToolbar={false}
          onAddContact={null}
          onChangeActType={handleChangeActType}
          bonusManagerProps={null}
          walkoutPotential={Number(offer?.walkout_potential) || 0}
          onEditPerformer={openPickerForEdit}
          calculated={calculated}
        />
      </div>

          <div style={{ marginTop: '16px' }}>
            <IncomeSection
              csrfToken={csrfToken}
              team={team}
              currentUser={currentUser}
              offer={offer}
              budget={budgetForComponents}
              calculated={calculated}
              updateBudget={handleUpdateBudget}
              onPassThroughChange={(on) => dispatch({
                type: V3_ACTIONS.UPDATE_OFFER,
                payload: { apply_tax_after_facility_fee: on },
              })}
              createTicketBudgetLineItem={handleCreateTicketLineItem}
              createIncomeBudgetLineItem={handleCreateIncomeLineItem}
              createAncillaryIncomeBudgetLineItem={handleCreateAncillaryIncomeLineItem}
              createBeforeAdjustedDeductionsBudgetLineItem={handleCreateBeforeAdjustedDeductionsLineItem}
              createAdditionBudgetLineItem={handleCreateAdditionLineItem}
              deleteBudgetLineItem={handleDeleteLineItem}
              autoSaveBudgetLineItem={handleAutoSaveLineItem}
              formatCalculatedNumber={formatCalculatedNumber}
              financeCategories={[]}
              settlementTickets={[]}
              addOnActuals={{}}
              performers={performers}
              onVisibilityChange={() => {}}
              showCurrencyConversion={showCurrencyConversion}
              globalCurrency={globalCurrency}
              reorderLineItem={reorderLineItem}
              onReorderLineItems={() => {}}
              allLineItems={lineItems}
              mobileOfferColumnOnly
            />
          </div>

          <div style={{ marginTop: '16px' }}>
            <ExpensesSection
              csrfToken={csrfToken}
              team={team}
              currentUser={currentUser}
              offer={offer}
              budget={budgetForComponents}
              calculated={calculated}
              updateBudget={handleUpdateBudget}
              createExpensesBudgetLineItem={handleCreateExpenseLineItem}
              createDeductionBudgetLineItem={handleCreateDeductionLineItem}
              createAncillaryExpensesBudgetLineItem={handleCreateAncillaryExpenseLineItem}
              deleteBudgetLineItem={handleDeleteLineItem}
              autoSaveBudgetLineItem={handleAutoSaveLineItem}
              formatCalculatedNumber={formatCalculatedNumber}
              financeCategories={[]}
              performers={performers}
              onVisibilityChange={() => {}}
              showCurrencyConversion={showCurrencyConversion}
              globalCurrency={globalCurrency}
              reorderLineItem={reorderLineItem}
              onReorderLineItems={() => {}}
              allLineItems={lineItems}
              mobileOfferColumnOnly
              siblingRows={siblingGuaranteeExpenses}
              onSelectSiblingOffer={handleSelectOffer}
            />
          </div>

          <div
            className="v3-mobile-summary-card"
            style={{
              marginTop: '16px',
              background: 'var(--od-c-ffffff, #fff)',
              borderRadius: '12px',
              border: '1px solid var(--od-c-d5d7da, #D5D7DA)',
              padding: '12px',
            }}
          >
            <button
              type="button"
              onClick={() => setSummaryExpanded((v) => !v)}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--od-c-333333, #333)',
                padding: 0,
              }}
            >
              <span>Financial Summary</span>
              <i className={`far ${summaryExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
            </button>
            {summaryExpanded && (
              <div style={{ marginTop: '12px' }}>
                <V3FinancialSummarySection
                  calculated={calculated}
                  team={team}
                  offer={offer}
                  budget={budgetForMath}
                  eventVenueNet={eventVenueNet}
                  showCurrencyConversion={showCurrencyConversion}
                  globalCurrency={globalCurrency}
                  eventOffers={eventOffers?.length > 0 ? eventOffers : [offer]}
                  embedded
                />
              </div>
            )}
          </div>

      <V3MobileLineupReorderSheet
        open={reorderSheetOpen}
        onClose={() => setReorderSheetOpen(false)}
        lineup={offer?.lineup || []}
        activeOfferId={offer?.id}
        onSelectOffer={handleSelectOffer}
        onAddArtist={openPickerForAdd}
        onReorder={handleReorder}
      />

      <V3MobileActionSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        groups={sheetGroups}
        currencyToggleProps={currencyToggleProps}
      />

      <V3MobilePerformerPickerSheet
        open={pickerOpen}
        onClose={closePicker}
        currentPerformer={pickerMode === 'edit' ? offer?.primary_performer : null}
        mode={pickerMode}
        onSubmit={handlePickerSubmit}
        teamId={team?.id}
      />

      <V3MobileCreateHoldSheet
        open={holdSheetOpen}
        onClose={() => setHoldSheetOpen(false)}
        team={team}
        offer={offer}
        csrfToken={csrfToken}
        rooms={team?.rooms || []}
        venueOwnerships={team?.venue_ownerships || []}
        existingHoldPositions={confirm?.type === 'Hold' ? (confirm?.hold_positions || []) : []}
        onSubmit={async (payload) => {
          await handleCreateHold(payload);
          setHoldSheetOpen(false);
        }}
      />

      <V3SendOfferModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        sendTo={sendTo}
        onChangeSendTo={setSendTo}
        sendMessage={sendMessage}
        onChangeSendMessage={setSendMessage}
        isSendingOffer={isSendingOffer}
        onSubmit={handleSubmitSend}
      />

      <CopyOfferModal
        isOpen={copyModalOpen}
        isCopying={isCopying}
        offer={offer}
        confirm={confirm}
        team={team}
        onClose={handleCancelCopy}
        onCopy={handleSubmitCopy}
      />

      {confirm && eventDetailsOpen && (
        <EventDetails
          csrfToken={csrfToken}
          team={team}
          confirm={confirm}
          venueOwnerships={team.venue_ownerships}
          rooms={team.rooms}
          currentUser={currentUser}
          openOnMount
          onClose={() => setEventDetailsOpen(false)}
          onConfirmSaved={(updatedConfirm) => {
            dispatch({ type: V3_ACTIONS.SET_OFFER, payload: { ...offer, calendar_event: updatedConfirm } });
            setEventDetailsOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default V3FinancePageMobile;
